"use client";

import { useState } from "react";
import {
  Search,
  Bell,
  Menu,
  Sun,
  Moon,
  CheckCircle2,
  AlertTriangle,
  Info,
  Plus,
  FileText,
  Building2,
  FolderKanban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { CommandMenu } from "@/components/command-menu";
import { MyAppsPopover } from "@/components/dashboard/my-apps";
import { useTheme } from "@/providers/theme-provider";
import { useAuth } from "@/providers/auth-provider";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/navigation";

interface Notification {
  id: string;
  type: "success" | "warning" | "info";
  title: string;
  message: string;
  date: number;
  link: string;
}

function relativeDate(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 172800) return "hier";
  return `${Math.floor(diff / 86400)} j`;
}

function NotificationBell() {
  const { data, isLoading } = useQuery<{
    notifications: Notification[];
    count: number;
  }>({
    queryKey: ["notifications"],
    queryFn: async () => {
      const res = await fetch("/api/notifications");
      if (!res.ok) return { notifications: [], count: 0 };
      return res.json();
    },
    refetchInterval: 30_000,
    retry: false,
  });

  const notifications = data?.notifications ?? [];
  const count = notifications.length;

  const typeIcon = (type: string) => {
    if (type === "success")
      return (
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
      );
    if (type === "warning")
      return (
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
      );
    return <Info className="h-3.5 w-3.5 shrink-0 text-blue-500" />;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-5 w-5 rounded-full p-0 text-[10px]"
            >
              {count > 9 ? "9+" : count}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-3 pb-2">
          <h4 className="text-sm font-semibold">Notifications</h4>
          {count > 0 && (
            <span className="text-xs text-muted-foreground">
              {count} alerte{count > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <DropdownMenuSeparator />
        {isLoading && (
          <div className="p-3 text-center text-xs text-muted-foreground">
            Chargement...
          </div>
        )}
        {!isLoading && notifications.length === 0 && (
          <div className="p-4 text-center text-xs text-muted-foreground">
            Aucune notification
          </div>
        )}
        {notifications.map((notif) => (
          <DropdownMenuItem key={notif.id} asChild className="p-0">
            <Link
              href={notif.link}
              className="flex cursor-pointer items-start gap-2.5 p-3"
            >
              {typeIcon(notif.type)}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium">{notif.title}</div>
                <div className="mt-0.5 truncate text-xs leading-tight text-muted-foreground">
                  {notif.message}
                </div>
              </div>
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {relativeDate(
                  typeof notif.date === "number"
                    ? notif.date
                    : Math.floor(new Date(notif.date).getTime() / 1000)
                )}
              </span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header() {
  const [commandOpen, setCommandOpen] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const { isAdmin } = useAuth();
  const router = useRouter();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 lg:px-6">
      <SidebarTrigger className="md:hidden">
        <Menu className="h-5 w-5" />
      </SidebarTrigger>

      <div className="flex flex-1 items-center gap-4">
        <button
          onClick={() => setCommandOpen(true)}
          className="hidden w-full max-w-sm items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors md:flex"
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">Rechercher...</span>
          <kbd className="pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 flex">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setCommandOpen(true)}
        >
          <Search className="h-5 w-5" />
        </Button>
        <CommandMenu open={commandOpen} onOpenChange={setCommandOpen} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8">
              <Plus className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => router.push("/devis/nouveau")}>
              <FileText className="mr-2 h-4 w-4 text-blue-500" />
              Nouveau devis
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push("/tiers/nouveau")}>
              <Building2 className="mr-2 h-4 w-4 text-emerald-500" />
              Nouveau client
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => router.push("/chantiers/nouveau")}>
              <FolderKanban className="mr-2 h-4 w-4 text-purple-500" />
              Nouveau chantier
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <MyAppsPopover isAdmin={isAdmin} />

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="text-muted-foreground hover:text-foreground"
        >
          {resolvedTheme === "dark" ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>

        <NotificationBell />
      </div>
    </header>
  );
}
